import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import cn from "classnames";
import { useState } from "react";
import useClaimBusinessStore from "store/useClaimBusinessStore";

// components
import { Alert } from "@trussworks/react-uswds";
import { Input, Button } from "components/LibraryComponents/Components";
import ClaimedDialog from "./ClaimedDialog";

// utils
import { validateClaimBusiness } from "utils/helper";

// styles
import styles from "./ClaimBusiness.module.scss";

// mock data 
import { MOCK_SUCCESS_CB_RESULTS, MOCK_EXISTING_CB_RESULTS } from "data/claim-business-mock-results";

export default function ClaimBusiness() {
  const [formData, setFormData] = useState({
    uei: "",
    cageCode: "",
    // bankAccount: "",
    tin: "",
  });

  const [error, setError] = useState({
    uei: "",
    cageCode: "",
    // bankAccount: "",
    tin: "",
  });

  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });
  };

  const { setData, setClaimRequestId } = useClaimBusinessStore();

  const handleSubmit = async () => {
    const validate = validateClaimBusiness(formData);

    if (Object.values(validate).some((value) => value !== "")) {
      setError(validate);
      return;
    }

    setError({
      uei: "",
      cageCode: "",
      // bankAccount: "",
      tin: "",
    });

    setIsLoading(true);

    // reset not found
    if (notFound) {
      setNotFound(false);
    }

    // Make API call to check if the business exists to '/_api/ucp/validate-sam' as a post request with these fields (uei, cageCode as cage_code, and tin)
    try {
      const response = await fetch("/_api/ucp/validate-sam", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uei: formData.uei,
          cage_code: formData.cageCode,
          // bank_account: formData.bankAccount,
          tin: formData.tin,
        }),
      });

      const data = await response.json();
      
      // create mock promise and return data
      // await new Promise((resolve) => setTimeout(resolve, 500));
      // const data = MOCK_EXISTING_CB_RESULTS;
      
      setIsLoading(false);

      // Handle the response data
      if (data.result === "found") {
        setData(data.data);
        setClaimRequestId(data.claim_request_id);
        setNotFound(false);
        navigate("/verify-business");
      } else if (data.result === "already_claimed") {
        setNotFound(false);
        setOpen(true);
      } else if (data.result === "not_found") {
        setNotFound(true);
      }
    } catch (error) {
      console.error("Error validating business:", error);
      setNotFound(true);
      setIsLoading(false);
    }
    // // Mock Test case - success
    // if (formData.uei === "K1J4QVAUMA78") {
    //   setNotFound(false);
    //   navigate("/verify-business");
    // }
    // // Mock Test case - Already claimed cases
    // else if (formData.uei === "J2E4QVAUMA78") {
    //   setNotFound(false);
    //   setOpen(true);
    // } else {
    //   setNotFound(true);
    // }
  };

  return (
    <>
      <Helmet>
        <title>Claim Business | Small Business Search</title>
      </Helmet>

      <div className={styles["claim-business-root"]}>
        <h1 className={styles["heading"]}>Claim your business</h1>

        <div className={styles["main-content"]}>
          <div className={styles["space-y-2"]}>
            <p className={styles["desc"]}>
              Claim ownership of your small business(es) with your UEI, CAGE
              code, and TIN to edit and enhance your business profile page. You
              can claim all businesses associated with the same TIN at the same
              time.
            </p>

            <Alert type="warning" headingLevel={"h4"} className={styles["alert-wrapper"]}>
              <p className={styles["alert-text"]}>
                Claiming your business in either SBS or MySBA Certifications links your Okta account to that UEI in <strong>both systems</strong>.
              </p>
            </Alert>

            <Alert
              type={"info"}
              headingLevel={"h4"}
              className={styles["alert-wrapper"]}
            >
              <p className={styles["alert-heading"]}>
                Before claiming your business
              </p>
              <ul className={styles["alert-list"]}>
                <li className={styles["alert-list-item"]}>
                  <strong>
                    This process must be completed by the Primary Qualifying
                    Owner of the business.
                  </strong>{" "}
                  A business may only be claimed by one account.
                </li>
                <li className={styles["alert-list-item"]}>
                  <strong>Verify your business ownership with SAM.gov.</strong>{" "}
                  Ensure your SAM.gov account is current and active.
                </li>
                <li>
                  NOTE: The TIN should be the EIN or SSN you used to register at <a href="https://sam.gov" target="_blank" rel="noreferrer">SAM.gov</a>, and is updated 3-4 business days after the business has been ACTIVATED by SAM.  Questions about your TIN should be referred to the <a href="https://www.fsd.gov/gsafsd_sp" target="_blank" rel="noreferrer">Federal Service Desk (FSD)</a>.
                </li>
              </ul>
              <p className={styles["alert-text"]}>
                For more details about claiming your business and to get help
                troubleshooting any errors, see the{" "}
                <a
                  href="https://sbaone.atlassian.net/wiki/external/YzNlMzNmMGJjZGU5NGY5YWE5Mjg2NGUyN2U2MzJhODc#Claim-your-business"
                  target="_blank"
                  rel="noreferrer"
                >
                  Claim your business
                </a>{" "}
                section of the SBS help guide.
              </p>
            </Alert>

            {/* <Link to="/" className={styles["link"]}>
              Learn more about verifying your business with SAM.gov and being a
              Qualifying Owner in our help center here.
            </Link> */}

            <p className={cn(styles["desc"], styles["italic"])}>
              A red asterisk (<em className={styles["asterisk"]}>*</em>)
              indicates a required field.
            </p>
          </div>

          <div className={styles["control-section"]}>
            <div className={styles["group"]}>
              <label htmlFor="uei" className={styles["label"]}>
                Unique Entity ID (UEI) <em className={styles["asterisk"]}>*</em>
              </label>
              <p className={styles["info-text"]}>
                12 alphanumeric characters with no spaces.
              </p>
              <Input
                id="uei"
                name="uei"
                type="text"
                placeholder="eg: 0123456789XX"
                value={formData.uei}
                onChange={handleChange}
                maxLength={12}
                required
                disabled={isLoading}
              />

              {error.uei && (
                <span className={styles["error-text"]}>{error.uei}</span>
              )}
            </div>

            <div className={styles["group"]}>
              <label htmlFor="cageCode" className={styles["label"]}>
                CAGE code
              </label>
              <p className={styles["info-text"]}>
                If you have multiple, enter any valid one. Must be 5
                alphanumeric characters.
              </p>
              <Input
                id="cageCode"
                name="cageCode"
                type="text"
                placeholder="eg: X1234"
                value={formData.cageCode}
                onChange={handleChange}
                maxLength={5}
                required
                disabled={isLoading}
              />
              {error.cageCode && (
                <span className={styles["error-text"]}>{error.cageCode}</span>
              )}
            </div>

            {/* <div className={styles["group"]}>
              <label htmlFor="bankAccount" className={styles["label"]}>
                Bank account number <em>*</em>
              </label>
              <p className={styles["info-text"]}>
                Enter the bank account number exactly as it was provided to
                SAM.gov. SBA encrypts this number, and will only use it for
                confirmation.
              </p>
              <Input
                id="bankAccount"
                name="bankAccount"
                type="number"
                placeholder="eg: 0112345678"
                value={formData.bankAccount}
                onChange={handleChange}
                required
              />
              {error.bankAccount && (
                <span className={styles["error-text"]}>
                  {error.bankAccount}
                </span>
              )}
            </div> */}

            <div className={styles["group"]}>
              <label htmlFor="tin" className={styles["label"]}>
                Taxpayer Identification Number (TIN){" "}
                <em className={styles["asterisk"]}>*</em>
              </label>
              <p className={styles["info-text"]}>
                9 numeric digits with no spaces or hyphens.
              </p>
              <Input
                id="tin"
                name="tin"
                type="number"
                placeholder="eg: 0112345678"
                value={formData.tin}
                onChange={handleChange}
                maxLength={9}
                required
                disabled={isLoading}
              />
              {error.tin && (
                <span className={styles["error-text"]}>{error.tin}</span>
              )}
            </div>
          </div>

          {notFound && <NotFoundAlert />}

          <div className={styles["button-wrapper"]}>
            <Button
              type="button"
              className={styles["submit-btn"]}
              onClick={handleSubmit}
              loading={isLoading}
              disabled={isLoading}
            >
              Find this business
            </Button>
          </div>
        </div>
      </div>

      {open && <ClaimedDialog open={open} setOpen={setOpen} />}
    </>
  );
}

function NotFoundAlert() {
  return (
    <div className={styles["not-found"]}>
      <Alert
        type={"error"}
        headingLevel={"h4"}
        className={styles["alert-wrapper"]}
      >
        <p className={styles["alert-heading"]}>No business found</p>
        <p className={styles["alert-text"]}>
          We were not able to find a business with the information provided
          above. Double check that you entered everything correctly, and that
          your business is verified with SAM.gov, and review troubleshooting
          steps on the{" "}
          <a
            href="https://sbaone.atlassian.net/wiki/external/YzNlMzNmMGJjZGU5NGY5YWE5Mjg2NGUyN2U2MzJhODc#Claim-your-business"
            rel="noreferrer"
            target="_blank"
          >
            Claim your business
          </a>{" "}
          section of the SBS help guide.
        </p>
      </Alert>
    </div>
  );
}
